/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import Context from './Context';
import {EventEmitter} from 'events';
import qs from 'querystring';
import Route from './Route';

const TRIGGER_BUFFER = new Set();

export default class Router extends EventEmitter {
  constructor() {
    super();
    this.callbacks = [];
  }

  add(path, ...callbacks) {
    let route = new Route(path);
    for (let fn of callbacks) {
      this.callbacks.push(route.getCallback(fn));
    }
  }

  use(path, fn) {
    if (typeof path !== 'string') {
      fn = path;
      path = '';
    }

    let handle = (ctx, next) => {
      // If the handler is a promise, wait for it to resolve.
      if (typeof fn.then === 'function') {
        return fn.then(f => {
          fn = f.default || f;
          return handle(ctx, next);
        });
      }

      // If the handler is a Router instance, propagate change events.
      if (fn instanceof Router) {
        fn.on('change', (p, replace, useRoot) => this.trigger(useRoot ? p : path + p, replace, useRoot));
        fn = fn.dispatch.bind(fn);
      }

      let pathname = ctx.params[0];
      ctx.querystring && (pathname += `?${ctx.querystring}`);
      ctx.hash && (pathname += `#${ctx.hash}`);
      return fn(pathname, ctx.context);
    };

    this.add(`${path}(.*)`, handle);
  }

  dispatch(path, context) {
    let i = 0;

    let next = () => {
      let ctx = new Context(path, context, this);
      let fn = this.callbacks[i++];
      if (!fn) {
        return;
      }

      return fn(ctx, next);
    };

    return next();
  }

  /**
   * Check if any routes match the provided `path` argument.
   * @param {string} path The path to check against the routes.
   * @returns {Route=} Route component is returned if there is a match.
   */
  match(path) {
    const ctx = new Context(path, {}, this);
    // Loop through all callbacks until one matches and returns a non-false
    // value. This is when we know there is a match.
    for (let cb of this.callbacks) {
      let comp = cb(ctx, () => false);
      if (comp) {
        return comp;
      }
    }
  }

  trigger(path, replace = false, useRoot = false) {
    if (typeof path === 'object') {
      // Remove empty keys from query
      let query = Object.assign({}, path.query);
      for (let key in query) {
        let val = query[key];
        if (val === '' || val == null || (typeof val === 'object' && Object.keys(val).length === 0)) {
          delete query[key];
        }
      }

      let q = qs.stringify(query);
      path = path.path + (q ? '?' + q : '');
    }

    let routeString = path + replace + useRoot;
    let buffed = TRIGGER_BUFFER.has(routeString);

    if (!buffed) {
      TRIGGER_BUFFER.add(routeString);
      setTimeout(() => TRIGGER_BUFFER.delete(routeString), 250);
      this.emit('change', path, replace, useRoot);
    }
  }

  redirect(from, to) {
    this.add(from, () => this.trigger(to, true));
  }
}
